import { useState } from 'react';
import {
  format,
  addDays,
  addMonths,
  parseISO,
  startOfISOWeek,
  endOfISOWeek,
  getISOWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import type { MyWorkordersView } from '@/features/user-preferences/user-preferences.hooks';

interface DateNavigationProps {
  date: string; // ISO date string
  onChange: (date: string) => void;
  mode?: MyWorkordersView;
}

export function DateNavigation({
  date,
  onChange,
  mode = 'day',
}: DateNavigationProps) {
  const currentDate = parseISO(date);
  const today = format(new Date(), 'yyyy-MM-dd');
  const isToday = date === today;
  const [open, setOpen] = useState(false);

  const stepDays = mode === 'week' ? 7 : 1;
  const step = (dir: 1 | -1) =>
    mode === 'month'
      ? addMonths(currentDate, dir)
      : addDays(currentDate, dir * stepDays);

  const goToDate = (newDate: Date) => {
    onChange(format(newDate, 'yyyy-MM-dd'));
  };

  const goToPrev = () => goToDate(step(-1));
  const goToNext = () => goToDate(step(1));
  const goToToday = () => onChange(today);

  const weekStart = startOfISOWeek(currentDate);
  const weekEnd = endOfISOWeek(currentDate);
  const weekNo = getISOWeek(currentDate);
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const title =
    mode === 'month'
      ? format(currentDate, 'MMMM yyyy')
      : mode === 'week'
        ? `Week ${weekNo}`
        : format(currentDate, 'EEEE');
  const subtitle =
    mode === 'month'
      ? `W${getISOWeek(monthStart)} – W${getISOWeek(monthEnd)}`
      : mode === 'week'
        ? `${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM yyyy')}`
        : format(currentDate, 'd MMMM yyyy');

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={goToPrev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={goToNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="hover:bg-accent h-auto flex-col items-center px-3 py-1"
          >
            <div className="flex items-center gap-1">
              <span className="text-lg font-semibold">{title}</span>
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            </div>
            <div className="text-muted-foreground text-sm">{subtitle}</div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={currentDate}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                goToDate(selectedDate);
                setOpen(false);
              }
            }}
          />
        </PopoverContent>
      </Popover>

      <Button
        variant={isToday ? 'secondary' : 'outline'}
        size="sm"
        onClick={goToToday}
        disabled={isToday}
      >
        <CalendarDays className="mr-2 h-4 w-4" />
        Today
      </Button>
    </div>
  );
}
